/**
 * AIOO O2O — all retail stores + Shopify pickup / store_inventory merge, store hours.
 */
(function (window) {
  'use strict';

  var STORE_COORDS = null;
  var STORE_COORDS_PROMISE = null;
  /** GPS coords only after explicit user action (nearest-store button); never requested on load. */
  var USER_GPS_COORDS = null;
  /** Rough lat/lng from IP lookup — no browser permission dialogs. */
  var APPROX_IP_COORDS = null;
  var APPROX_IP_PROMISE = null;
  var TZ = 'Europe/Riga';
  var PICKUP_READY_MIN = 60;
  var LS_STORE_KEY = 'aioo_preferred_store_v1';
  var O2O_LINE_PROP_KEYS = [
    'Gatavība',
    'Piegādes veids',
    'Saņemšanas veikals',
    'Produkts',
  ];
  /** Session-scoped cache for IP geolocation (avoids ipwho.is on every PDP load in the same tab). */
  var SS_IP_APPROX_KEY = 'aioo_o2o_ip_approx_v1';
  var MOBILE_MQ = window.matchMedia('(max-width: 767px)');
  /** Fallback for distance sort when IP/GPS unavailable (Rīga centrs). */
  var RIGA_CENTER = { lat: 56.9496, lng: 24.1052 };
  /** Saved store ignored if farther than nearest by this margin (km). */
  var SAVED_STORE_NEAR_KM = 8;
  /** Online warehouse entry in stores.json — not a retail pickup point. Same
   *  name/filter as templates/page.stores.liquid and generate_geo_schema.py. */
  var WAREHOUSE_NAME = 'AIOO Noliktavā';

  function normName(s) {
    return (s || '')
      .toLowerCase()
      .replace(/^aioo\s+/i, '')
      .replace(/^mobilo\s+/i, '')
      .replace(/[^a-z0-9āčēģīķļņšūž\s]/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function cityFromAddress(addr) {
    if (!addr) return '';
    var parts = String(addr).split(',');
    if (parts.length < 2) return '';
    return parts[1].replace(/lv-\d+/gi, '').trim().toLowerCase();
  }

  function haversineKm(lat1, lon1, lat2, lon2) {
    var R = 6371;
    var dLat = ((lat2 - lat1) * Math.PI) / 180;
    var dLon = ((lon2 - lon1) * Math.PI) / 180;
    var a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function loadStoreCoords(url) {
    if (STORE_COORDS) return Promise.resolve(STORE_COORDS);
    if (STORE_COORDS_PROMISE) return STORE_COORDS_PROMISE;
    STORE_COORDS_PROMISE = fetch(url)
      .then(function (r) {
        return r.json();
      })
      .then(function (list) {
        // Exclude the online warehouse: it is not a pickup point a customer
        // can walk into, but it carries store hours in stores.json like any
        // retail row, so the pickup widget was showing "get it today" there.
        var retail = (list || []).filter(function (s) {
          return s.name !== WAREHOUSE_NAME;
        });
        STORE_COORDS = { list: retail, byName: {} };
        retail.forEach(function (s) {
          var key = normName(s.name);
          if (key) STORE_COORDS.byName[key] = s;
        });
        return STORE_COORDS;
      })
      .catch(function () {
        STORE_COORDS = { list: [], byName: {} };
        return STORE_COORDS;
      });
    return STORE_COORDS_PROMISE;
  }

  function coordsForStore(store, coordsMap) {
    var key = normName(store.name);
    if (coordsMap.byName[key]) return coordsMap.byName[key];
    var keys = Object.keys(coordsMap.byName);
    for (var i = 0; i < keys.length; i++) {
      if (key.indexOf(keys[i]) >= 0 || keys[i].indexOf(key) >= 0) {
        return coordsMap.byName[keys[i]];
      }
    }
    return { lat: store.lat, lng: store.lng, raw: store };
  }

  function coordsForSorting() {
    return USER_GPS_COORDS || APPROX_IP_COORDS || RIGA_CENTER;
  }

  /**
   * IP-based lat/lng (~region). No navigator.geolocation.
   * CORS: ipwho.is responds with Access-Control-Allow-Origin: * (verified 2026-05, CF edge RIX).
   * Future: if rate limits bite, use a Shopify metafield/app proxy or Liquid localization for country/city
   * (precise coords inside LV still need an API or user GPS).
   */
  function readApproxIpFromSession() {
    try {
      var raw = sessionStorage.getItem(SS_IP_APPROX_KEY);
      if (raw == null || raw === '') return undefined;
      var o = JSON.parse(raw);
      if (o && o.v === 1 && o.lat != null && o.lng != null) {
        return { lat: Number(o.lat), lng: Number(o.lng) };
      }
      if (o && o.v === 1 && o.none) {
        return null;
      }
    } catch (e) {
      /* ignore */
    }
    return undefined;
  }

  function persistApproxIpSession(coordsOrNull) {
    try {
      if (coordsOrNull == null) {
        sessionStorage.setItem(SS_IP_APPROX_KEY, JSON.stringify({ v: 1, none: true }));
      } else {
        sessionStorage.setItem(
          SS_IP_APPROX_KEY,
          JSON.stringify({ v: 1, lat: coordsOrNull.lat, lng: coordsOrNull.lng })
        );
      }
    } catch (e) {
      /* private mode / quota */
    }
  }

  function fetchApproxCoordsByIp() {
    if (APPROX_IP_PROMISE) return APPROX_IP_PROMISE;

    var cached = readApproxIpFromSession();
    if (cached !== undefined) {
      APPROX_IP_COORDS = cached;
      APPROX_IP_PROMISE = Promise.resolve(cached);
      return APPROX_IP_PROMISE;
    }

    var ipFetchOpts = { credentials: 'omit', cache: 'default' };
    if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
      ipFetchOpts.signal = AbortSignal.timeout(6500);
    }
    APPROX_IP_PROMISE = fetch('https://ipwho.is/me', ipFetchOpts)
      .then(function (r) {
        return r.json();
      })
      .then(function (j) {
        if (j && j.success && j.latitude != null && j.longitude != null) {
          APPROX_IP_COORDS = { lat: Number(j.latitude), lng: Number(j.longitude) };
          persistApproxIpSession(APPROX_IP_COORDS);
          return APPROX_IP_COORDS;
        }
        APPROX_IP_COORDS = null;
        persistApproxIpSession(null);
        return null;
      })
      .catch(function () {
        APPROX_IP_COORDS = null;
        persistApproxIpSession(null);
        return null;
      });

    return APPROX_IP_PROMISE;
  }

  /** Only for [data-o2o-nearest] click — triggers browser permission UI in context of user gesture. */
  function requestBrowserGeolocation() {
    if (!navigator.geolocation) return Promise.resolve(null);
    return new Promise(function (resolve) {
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          USER_GPS_COORDS = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          resolve(USER_GPS_COORDS);
        },
        function () {
          resolve(null);
        },
        { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 }
      );
    });
  }

  function readShopifyPayload(root) {
    var el = root.querySelector('[data-o2o-shopify-json]');
    if (!el) {
      return {
        locations: [],
        onlineAvailable: false,
        pickupNowCount: 0,
        isUsedPhone: false,
        variantInventories: {},
        siblingVariants: [],
      };
    }
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return {
        locations: [],
        onlineAvailable: false,
        pickupNowCount: 0,
        isUsedPhone: false,
        variantInventories: {},
        siblingVariants: [],
      };
    }
  }

  function getRigaNow() {
    return new Date(new Date().toLocaleString('en-US', { timeZone: TZ }));
  }

  function parseMinutes(hhmm) {
    var m = String(hhmm || '').match(/(\d{1,2}):(\d{2})/);
    if (!m) return null;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  }

  function parseHoursRange(str) {
    if (!str || str === 'same') return null;
    var parts = String(str).split(/\s*-\s*/);
    if (parts.length < 2) return null;
    var open = parseMinutes(parts[0]);
    var close = parseMinutes(parts[1]);
    if (open == null || close == null) return null;
    return { open: open, close: close };
  }

  function hoursForDate(store, date) {
    var day = date.getDay();
    var sat = store.hours_saturday;
    var sun = store.hours_sunday;
    if (day === 0 && sun) {
      if (sun === 'same') return parseHoursRange(store.hours);
      return parseHoursRange(sun);
    }
    if (day === 6 && sat) {
      if (sat === 'same') return parseHoursRange(store.hours);
      return parseHoursRange(sat);
    }
    return parseHoursRange(store.hours);
  }

  function isStoreOpen(store, now) {
    var range = hoursForDate(store, now);
    if (!range) return true;
    var mins = now.getHours() * 60 + now.getMinutes();
    if (range.close <= range.open) {
      return mins >= range.open || mins < range.close;
    }
    return mins >= range.open && mins < range.close;
  }

  function minutesUntilClose(store, now) {
    var range = hoursForDate(store, now);
    if (!range) return PICKUP_READY_MIN;
    var mins = now.getHours() * 60 + now.getMinutes();
    if (range.close <= range.open) {
      if (mins >= range.open) return range.close + 24 * 60 - mins;
      return range.close - mins;
    }
    return range.close - mins;
  }

  function opensAtLabel(store, now) {
    var range = hoursForDate(store, now);
    if (!range) return '';
    var openH = Math.floor(range.open / 60);
    var openM = range.open % 60;
    var pad = openM < 10 ? '0' + openM : String(openM);
    return 'Atvērts plkst. ' + openH + ':' + pad;
  }

  function loadPreferredStore() {
    try {
      var raw = localStorage.getItem(LS_STORE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }

  function savePreferredStore(selection) {
    if (!selection || !selection.normName) return;
    try {
      localStorage.setItem(
        LS_STORE_KEY,
        JSON.stringify({
          normName: selection.normName,
          storeName: selection.storeName,
          displayName: selection.displayName,
          savedAt: Date.now(),
        })
      );
    } catch (e) {
      /* private mode / quota */
    }
  }

  function findRowIndexByNormName(rows, norm) {
    if (!rows || !norm) return -1;
    for (var i = 0; i < rows.length; i++) {
      if (normName(rows[i].store.name) === norm) return i;
    }
    return -1;
  }

  function findNearestOrderableIndex(rows) {
    if (!rows) return -1;
    var best = -1;
    var bestDist = Infinity;
    for (var i = 0; i < rows.length; i++) {
      if (!isOrderableStatus(rows[i].status)) continue;
      if (rows[i].dist == null) continue;
      if (rows[i].dist < bestDist) {
        bestDist = rows[i].dist;
        best = i;
      }
    }
    return best;
  }

  function updateNearestBadges(root) {
    var nearIdx = findNearestOrderableIndex(root._o2oLastRows);
    root._o2oNearestIndex = nearIdx;
    var listEl = root.querySelector('[data-o2o-store-list]');
    if (!listEl) return;
    listEl.querySelectorAll('[data-nearest-badge]').forEach(function (badge) {
      badge.hidden = true;
      badge.classList.add('hidden');
    });
    if (nearIdx < 0) return;
    var li = listEl.querySelector('[data-row-index="' + nearIdx + '"]');
    if (!li) return;
    var badge = li.querySelector('[data-nearest-badge]');
    if (badge) {
      badge.hidden = false;
      badge.classList.remove('hidden');
    }
  }

  function shouldPreferSavedStore(saved, rows, nearIdx) {
    if (!saved || !saved.normName || nearIdx < 0) return false;
    var savedIdx = findRowIndexByNormName(rows, saved.normName);
    if (savedIdx < 0 || !isOrderableStatus(rows[savedIdx].status)) return false;
    var nearDist = rows[nearIdx].dist;
    var savedDist = rows[savedIdx].dist;
    if (nearDist == null || savedDist == null) return true;
    return savedDist <= nearDist + SAVED_STORE_NEAR_KM;
  }

  function computeStockTier(status, qty) {
    if (status === 'unavailable') return 'none';
    if (status === 'transfer') return 'order';
    if (status === 'now' || status === 'closed_stock') {
      if (qty === 1) return 'low';
      return 'high';
    }
    return 'order';
  }

  function stockDotHtml(tier) {
    var label =
      tier === 'high'
        ? 'Krājumā'
        : tier === 'low'
          ? 'Pēdējais'
          : tier === 'order'
            ? 'Pasūtījums'
            : 'Nav';
    return (
      '<span class="o2o-stock-dot o2o-stock-dot--' +
      tier +
      '" aria-hidden="true"></span>' +
      '<span class="v-hidden">' +
      escapeHtml(label) +
      '</span>'
    );
  }

  function locationMatchKeys(store) {
    // `store.store` (mall/chain brand, e.g. "T/C Rimi") is shared by multiple
    // AIOO kiosks in DIFFERENT cities — using it alone as a match key against
    // Shopify location names caused every "T/C Rimi" kiosk to report the stock
    // of any other "T/C Rimi" kiosk (e.g. Madona's stock showing as available
    // in Dobele/Tukums/Jūrmala too). Scope it to the city so it only matches
    // the one physical location it actually names.
    var keys = [];
    var street = normName((store.address || '').split(',')[0]);
    var city = normName(cityFromAddress(store.address));
    var mall = normName(store.store || '');
    if (street && street.length > 6) keys.push(street);
    if (city && mall) keys.push(city + ' ' + mall);
    return keys;
  }

  function namesMatchLocation(keys, candidateName) {
    var cn = normName(candidateName || '');
    if (!cn || !keys.length) return false;
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (!key) continue;
      if (cn === key || cn.indexOf(key) >= 0) return true;
    }
    return false;
  }

  function matchShopifyLocation(store, shopifyLocs) {
    var keys = locationMatchKeys(store);
    var street = normName((store.address || '').split(',')[0]);

    for (var i = 0; i < shopifyLocs.length; i++) {
      var av = shopifyLocs[i];
      if (namesMatchLocation(keys, av.name)) return av;
      if (av.address1 && street && street.length > 6 && normName(av.address1).indexOf(street) >= 0) {
        return av;
      }
    }
    return null;
  }

  function matchInventoryRow(store, row) {
    if (!row) return false;
    return namesMatchLocation(locationMatchKeys(store), row.name || '');
  }

  function stockQtyAtStore(store, inventory) {
    var qty = 0;
    (inventory || []).forEach(function (row) {
      if (matchInventoryRow(store, row)) {
        qty += parseInt(row.qty, 10) || 0;
      }
    });
    return qty;
  }

  function hasPickupStock(store, shopifyLocs, inventory) {
    var matched = matchShopifyLocation(store, shopifyLocs);
    if (matched && matched.available) return true;
    return stockQtyAtStore(store, inventory) > 0;
  }

  function getVariantSource(payload, variantId) {
    var map = payload.variantInventories || {};
    var key = String(variantId || payload.variantId || '');
    var cur = map[key] || {
      inventory: [],
      locations: [],
      option1: null,
      option2: null,
      option3: null,
    };
    var inv = (cur.inventory || []).slice();
    var locs = (cur.locations || []).concat(payload.locations || []);

    return { inventory: inv, locations: locs };
  }

  function resolveStatus(store, shopifyLocs, inventory, onlineAvailable, now) {
    var matched = matchShopifyLocation(store, shopifyLocs);
    var hasStock = onlineAvailable && hasPickupStock(store, shopifyLocs, inventory);

    if (hasStock) {
      if (isStoreOpen(store, now)) {
        var untilClose = minutesUntilClose(store, now);
        if (untilClose >= PICKUP_READY_MIN) {
          return { status: 'now', matched: matched, opensLabel: '' };
        }
        return {
          status: 'now',
          matched: matched,
          opensLabel: '',
          soonClose: true,
        };
      }
      return {
        status: 'closed_stock',
        matched: matched,
        opensLabel: opensAtLabel(store, now),
      };
    }
    if (onlineAvailable) {
      return { status: 'transfer', matched: matched, opensLabel: '' };
    }
    return { status: 'unavailable', matched: matched, opensLabel: '' };
  }

  function displayName(store) {
    var raw = store.name || '';
    if (raw.indexOf(' - ') > 0) return raw;
    var mall = store.store ? store.store + ', ' : '';
    return raw + (mall ? ' - ' + mall : '');
  }

  function titleCaseCity(city) {
    if (!city) return '';
    return city
      .split(/[\s-]+/)
      .map(function (part) {
        return part ? part.charAt(0).toUpperCase() + part.slice(1) : '';
      })
      .join(' ');
  }

  function cityForRow(row) {
    if (row.matched && row.matched.city) return titleCaseCity(String(row.matched.city).trim());
    var fromAddr = cityFromAddress(row.store && row.store.address);
    if (fromAddr) return titleCaseCity(fromAddr);
    return (typeof window !== 'undefined' && window.AIOO_CITY_OTHER) || 'Citi';
  }

  function venueLineForStore(store) {
    var mall = (store && store.store) || '';
    if (mall) return mall;
    var raw = (store && store.name) || '';
    if (raw.indexOf(' - ') > 0) return raw.split(' - ').slice(1).join(' - ').trim();
    return raw;
  }

  function addressLineForRow(row) {
    var store = row.store;
    if (row.matched && row.matched.address1) {
      var line = row.matched.address1;
      if (row.matched.city && line.indexOf(row.matched.city) < 0) {
        line += ', ' + row.matched.city;
      }
      return line;
    }
    var addr = (store && store.address) || '';
    if (!addr) return '';
    var parts = addr.split(',');
    if (parts.length >= 2) {
      return parts[0].trim() + (parts[1] ? ', ' + parts[1].trim() : '');
    }
    return addr;
  }

  function availabilityLabelForRow(row) {
    if (row.status === 'now') {
      return row.soonClose ? 'Saņem šodien · steidzami' : 'Saņem jau šodien';
    }
    if (row.status === 'closed_stock') {
      return 'Krājums · ' + (row.opensLabel || 'pēc atvēršanas');
    }
    if (row.status === 'transfer') {
      return '1–2 darba dienas';
    }
    return 'Nav pieejams';
  }

  function availabilityPillHtml(row) {
    var label = availabilityLabelForRow(row);
    var mod =
      row.status === 'now'
        ? 'now'
        : row.status === 'closed_stock'
          ? 'closed'
          : row.status === 'transfer'
            ? 'transfer'
            : 'none';
    return (
      '<span class="o2o-avail-pill o2o-avail-pill--' +
      mod +
      '">' +
      escapeHtml(label) +
      '</span>'
    );
  }

  function badgeForStatus(row) {
    return availabilityPillHtml(row);
  }

  function etaLabelForRow(row) {
    if (row.status === 'now') {
      return row.soonClose ? 'Uz vietas · ~1 stunda (steidzami)' : 'Uz vietas · ~1 stunda';
    }
    if (row.status === 'closed_stock') {
      return 'Krājums veikālā · ' + (row.opensLabel || 'pēc atvēršanas');
    }
    if (row.status === 'transfer') {
      return 'Piegāde uz veikalu · 1–2 dienas';
    }
    return '';
  }

  function rowMatchesListFilter(row, mode) {
    if (!mode || mode === 'all') return true;
    if (mode === 'now') return row.status === 'now' || row.status === 'closed_stock';
    if (mode === 'transfer') return row.status === 'transfer';
    return true;
  }

  function applyListFilters(root) {
    var q = (root._o2oSearchQuery || '').trim().toLowerCase();
    var mode = root._o2oListFilter || 'all';
    var listEl = root.querySelector('[data-o2o-store-list]');
    if (!listEl) return;
    var visibleByCity = {};
    listEl.querySelectorAll('[data-store-row]').forEach(function (li) {
      var idx = parseInt(li.getAttribute('data-row-index'), 10);
      var row = root._o2oLastRows && root._o2oLastRows[idx];
      if (!row) return;
      var show =
        rowMatchesListFilter(row, mode) && (!q || rowSearchText(row).indexOf(q) >= 0);
      li.classList.toggle('is-filtered-out', !show);
      if (show) {
        var city = li.getAttribute('data-city') || cityForRow(row);
        visibleByCity[city] = true;
      }
    });
    listEl.querySelectorAll('[data-city-header]').forEach(function (header) {
      var city = header.getAttribute('data-city-header');
      header.hidden = !visibleByCity[city];
    });
  }

  function renderModalFilters(root, counts) {
    var el = root.querySelector('[data-o2o-modal-filters]');
    if (!el) return;
    var mode = root._o2oListFilter || 'all';
    var nowN = (counts.now || 0) + (counts.closedStock || 0);
    var transferN = counts.transfer || 0;
    var total = counts.total || 0;
    var specs = [
      { id: 'all', label: 'Visi veikali', n: total },
      { id: 'now', label: 'Uz vietas', n: nowN },
      { id: 'transfer', label: '1–2 dienas', n: transferN },
    ];
    el.innerHTML = specs
      .map(function (spec) {
        var active = mode === spec.id;
        return (
          '<button type="button" class="o2o-modal__filter' +
          (active ? ' is-active' : '') +
          '" data-o2o-filter="' +
          spec.id +
          '" role="tab" aria-selected="' +
          (active ? 'true' : 'false') +
          '">' +
          escapeHtml(spec.label) +
          ' (' +
          spec.n +
          ')</button>'
        );
      })
      .join('');
  }

  function bindModalFilters(root) {
    if (root._o2oFilterDelegated) return;
    root._o2oFilterDelegated = true;
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-o2o-filter]');
      if (!btn || !root.contains(btn)) return;
      e.preventDefault();
      e.stopPropagation();
      root._o2oListFilter = btn.getAttribute('data-o2o-filter') || 'all';
      var el = root.querySelector('[data-o2o-modal-filters]');
      if (el) {
        el.querySelectorAll('[data-o2o-filter]').forEach(function (b) {
          var on = b.getAttribute('data-o2o-filter') === root._o2oListFilter;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }
      applyListFilters(root);
    });
    root.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var btn = e.target.closest('[data-o2o-filter]');
      if (!btn || !root.contains(btn)) return;
      e.preventDefault();
      root._o2oListFilter = btn.getAttribute('data-o2o-filter') || 'all';
      var el = root.querySelector('[data-o2o-modal-filters]');
      if (el) {
        el.querySelectorAll('[data-o2o-filter]').forEach(function (b) {
          var on = b.getAttribute('data-o2o-filter') === root._o2oListFilter;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
      }
      applyListFilters(root);
    });
  }

  function syncModalTitle(root) {
    var title = root.querySelector('[data-o2o-modal-title]');
    if (title) title.textContent = 'Pieejamība veikalos';
  }

  function heroMetaLine(c) {
    if (!c) return '';
    var nowN = (c.now || 0) + (c.closedStock || 0);
    var parts = [];
    if (nowN > 0) {
      parts.push(
        nowN +
          (nowN === 1 ? ' veikalā uz vietas' : ' veikalos uz vietas')
      );
    }
    if (c.transfer > 0) {
      parts.push(c.transfer + ' ar piegādi 1–2 d.');
    }
    if (!parts.length && c.online && c.total) {
      return c.total + (c.total === 1 ? ' veikals' : ' veikali') + ' · piegāde 1–2 d.';
    }
    if (!parts.length) return '';
    return parts.join(' · ');
  }

  function syncHeroFromSelection(root) {
    var headline = root.querySelector('[data-o2o-hero-headline]');
    var stats = root.querySelector('[data-o2o-hero-stats]');
    var c = root._o2oHeroCounts;
    if (!c) return;

    if (headline) headline.textContent = 'Pieejamība veikalos';
    if (stats) {
      stats.textContent = heroMetaLine(c);
    }
    // Lets the PDP pickup row (assets/pdp-mobile-ux.js) say how many stores really have the item.
    document.dispatchEvent(new CustomEvent('aioo:o2o:counts', {
      detail: { inStores: (c.now || 0) + (c.closedStock || 0), transfer: c.transfer || 0, online: !!c.online }
    }));
  }

  function isOrderableStatus(status) {
    return status === 'now' || status === 'closed_stock' || status === 'transfer';
  }

  function rowSearchText(row) {
    var s = row.store;
    return [
      s.name,
      s.store,
      s.address,
      displayName(s),
      row.city,
      row.matched && row.matched.city,
      row.matched && row.matched.address1,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  function genericOrderContext(root) {
    var c = root._o2oHeroCounts || {};
    var nowN = (c.now || 0) + (c.closedStock || 0);
    var eta =
      nowN > 0 && c.transfer > 0
        ? 'Uz vietas vai piegāde 1–2 dienas'
        : nowN > 0
          ? 'Saņem veikalā ~1 stunda'
          : 'Piegāde uz veikalu 1–2 dienas';
    return {
      displayName: 'AIOO veikals',
      etaLabel: eta,
    };
  }

  function syncModalFooter(root) {
    var bar = root.querySelector('[data-o2o-pickup]');
    var orderBtn = root.querySelector('[data-o2o-order]');
    if (bar) bar.hidden = false;
    if (orderBtn) orderBtn.disabled = false;
  }

  function updatePickupBar(root) {
    syncModalFooter(root);
  }

  function setSelectedStoreRow(root, index) {
    var listEl = root.querySelector('[data-o2o-store-list]');
    if (!listEl || !root._o2oLastRows) return;

    listEl.querySelectorAll('.o2o-store-row--selected').forEach(function (el) {
      el.classList.remove('o2o-store-row--selected');
      el.setAttribute('aria-selected', 'false');
    });

    if (index == null || index < 0) {
      root._o2oSelection = null;
      updatePickupBar(root);
      updateSavedStoreChip(root);
      syncHeroFromSelection(root);
      syncPrimaryCta(root);
      return;
    }

    var row = root._o2oLastRows[index];
    var li = listEl.querySelector('[data-row-index="' + index + '"]');
    if (!row || !li || !isOrderableStatus(row.status)) return;

    li.classList.add('o2o-store-row--selected');
    li.setAttribute('aria-selected', 'true');
    li.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    root._o2oSelection = {
      index: index,
      status: row.status,
      displayName: displayName(row.store),
      storeName: row.store.name || '',
      normName: normName(row.store.name),
      address: row.store.address || '',
      etaLabel: etaLabelForRow(row),
      stockTier: row.stockTier || computeStockTier(row.status, row.stockQty),
    };
    savePreferredStore(root._o2oSelection);
    updatePickupBar(root);
    updateSavedStoreChip(root);
    syncHeroFromSelection(root);
    syncPrimaryCta(root);
  }

  function autoSelectSmart(root) {
    if (!root._o2oLastRows) return;
    var nearIdx = findNearestOrderableIndex(root._o2oLastRows);
    var saved = loadPreferredStore();
    if (nearIdx >= 0) {
      if (shouldPreferSavedStore(saved, root._o2oLastRows, nearIdx)) {
        var savedIdx = findRowIndexByNormName(root._o2oLastRows, saved.normName);
        setSelectedStoreRow(root, savedIdx);
        return;
      }
      setSelectedStoreRow(root, nearIdx);
      return;
    }
    if (saved && saved.normName) {
      var fallbackIdx = findRowIndexByNormName(root._o2oLastRows, saved.normName);
      if (fallbackIdx >= 0 && isOrderableStatus(root._o2oLastRows[fallbackIdx].status)) {
        setSelectedStoreRow(root, fallbackIdx);
        return;
      }
    }
    autoSelectFirstOrderable(root);
  }

  function selectNearestStore(root) {
    var storesUrl = root.getAttribute('data-stores-json') || '';
    var btn = root.querySelector('[data-o2o-nearest]');
    function setBtnLoading(on) {
      if (!btn) return;
      btn.disabled = !!on;
      btn.setAttribute('aria-busy', on ? 'true' : 'false');
      btn.classList.toggle('is-loading', !!on);
    }
    setBtnLoading(true);
    Promise.all([loadStoreCoords(storesUrl), requestBrowserGeolocation()])
      .then(function (res) {
        var coordsMap = res[0];
        var coords = res[1] || coordsForSorting();
        buildStoreRows(root, coordsMap, coords, { skipAutoSelect: true });
        var idx = findNearestOrderableIndex(root._o2oLastRows);
        if (idx >= 0) {
          setSelectedStoreRow(root, idx);
          var search = root.querySelector('[data-o2o-search]');
          if (search) search.value = '';
          filterStoreRows(root, '');
        }
      })
      .finally(function () {
        setBtnLoading(false);
      });
  }

  function updateSavedStoreChip(root) {
    var chip = root.querySelector('[data-o2o-saved]');
    if (chip) chip.hidden = true;
    syncHeroFromSelection(root);
  }

  function getActiveVariantId(root) {
    var sectionId = root.getAttribute('data-section-id') || '';
    var sel = document.getElementById('ProductSelect-' + sectionId);
    if (sel && sel.value) {
      var fromForm = parseInt(sel.value, 10);
      if (fromForm) return fromForm;
    }
    return parseInt(root.getAttribute('data-variant-id'), 10) || 0;
  }

  function defaultPrimaryLabel(root) {
    var pickupNow = parseInt(root.getAttribute('data-pickup-now'), 10) || 0;
    var online = root.getAttribute('data-online-available') === 'true';
    if (pickupNow > 0) return 'Izvēlēties veikalu';
    if (online) return 'Izvēlēties veikalu';
    return 'Izvēlēties veikalu';
  }

  function primaryLabelForSelection(sel) {
    if (!sel) return 'Pasūtīt';
    if (sel.status === 'now') return 'Pasūtīt · ~1 stunda';
    if (sel.status === 'transfer') return 'Pasūtīt · 1–2 dienas';
    return 'Pasūtīt';
  }

  function syncPrimaryCta(root) {
    var label = root._o2oSelection
      ? primaryLabelForSelection(root._o2oSelection)
      : defaultPrimaryLabel(root);
    root.querySelectorAll('[data-o2o-primary]').forEach(function (btn) {
      btn.textContent = label;
    });
    syncStickyBar(root);
  }

  function setOrderButtonsLoading(root, loading) {
    root.querySelectorAll('[data-o2o-order], [data-o2o-primary], [data-o2o-sticky-action]').forEach(function (btn) {
      if (loading) {
        btn.disabled = true;
        btn.classList.add('is-loading');
        if (btn.hasAttribute('data-o2o-order')) {
          var labelEl = btn.querySelector('.o2o-btn__label');
          if (labelEl) {
            labelEl.innerHTML =
              '<span class="o2o-btn__spinner" aria-hidden="true"></span> Apstrādā…';
          }
        } else if (
          btn.hasAttribute('data-o2o-primary') ||
          btn.hasAttribute('data-o2o-sticky-action')
        ) {
          btn.textContent = 'Apstrādā…';
        }
      } else {
        btn.classList.remove('is-loading');
        if (btn.hasAttribute('data-o2o-order')) {
          btn.disabled = !root._o2oSelection;
          var lbl = btn.querySelector('.o2o-btn__label');
          if (lbl) lbl.textContent = 'Pasūtīt';
        } else {
          btn.disabled = false;
        }
      }
    });
  }

  function syncStickyBar(root) {
    var bar = root.querySelector('[data-o2o-sticky]');
    if (!bar) return;
    var sel = root._o2oSelection;
    var cta = bar.querySelector('[data-o2o-sticky-action]');
    var storeEl = bar.querySelector('[data-o2o-sticky-store]');

    if (sel) {
      if (cta) cta.textContent = primaryLabelForSelection(sel);
      if (storeEl) {
        storeEl.hidden = false;
        storeEl.textContent = sel.displayName + ' · ' + sel.etaLabel;
      }
    } else {
      if (cta) cta.textContent = defaultPrimaryLabel(root);
      if (storeEl) storeEl.hidden = true;
    }
  }

  function handlePrimaryAction(root) {
    if (root._o2oOpenModal) root._o2oOpenModal();
  }

  function initStickyBar(root) {
    if (root._o2oStickyBound) return;
    root._o2oStickyBound = true;
    var bar = root.querySelector('[data-o2o-sticky]');
    if (!bar) return;

    var anchor =
      document.querySelector('[data-product-single-media-group]') ||
      document.querySelector('.pr_lg_img') ||
      document.querySelector('.ptContainer');

    function setStickyVisible(show) {
      if (!MOBILE_MQ.matches || document.documentElement.classList.contains('o2o-modal-open')) {
        bar.hidden = true;
        bar.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('o2o-sticky-active');
        return;
      }
      bar.hidden = !show;
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
      document.documentElement.classList.toggle('o2o-sticky-active', show);
    }

    if (anchor) {
      var observer = new IntersectionObserver(
        function (entries) {
          setStickyVisible(!entries[0].isIntersecting);
        },
        { threshold: 0, rootMargin: '-8px 0px 0px 0px' }
      );
      observer.observe(anchor);
    }

    MOBILE_MQ.addEventListener('change', function () {
      if (!MOBILE_MQ.matches) setStickyVisible(false);
    });

    root._o2oHideSticky = function () {
      setStickyVisible(false);
    };
    root._o2oRefreshSticky = function () {
      if (anchor) {
        var rect = anchor.getBoundingClientRect();
        setStickyVisible(rect.bottom < 0 || rect.top < -anchor.offsetHeight * 0.5);
      }
    };
  }

  function activateCartStorePickup() {
    var pickup = document.getElementById('StorePickup');
    if (pickup && !pickup.classList.contains('active')) {
      pickup.click();
    }
    var ship = document.getElementById('Shipping');
    if (ship) {
      ship.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  function removeO2oCartHandoffNotice() {
    var el = document.getElementById('o2o-cart-handoff');
    if (el) el.remove();
  }

  function initO2oCartHandoff() {
    if (!/\/cart\/?$/.test(window.location.pathname)) return;

    removeO2oCartHandoffNotice();

    var isHandoff = isO2oCartHandoffUrl();
    if (isHandoff) {
      document.documentElement.classList.add('o2o-cart-handoff');
      hideO2oCartBanner();
    }

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        return stripO2oLinePropertiesFromCart(cart).then(function (stripped) {
          if (stripped) {
            window.location.reload();
            return null;
          }
          return cart;
        });
      })
      .then(function (cart) {
        if (!cart) return;
        var attrs = cart.attributes || {};
        var storeFromCart = attrs['Saņemšanas veikals'] || '';
        var cartHasO2o =
          attrs['Piegādes veids'] === 'Saņemšana veikalā (O2O)' || !!storeFromCart;
        if (!isHandoff && !cartHasO2o) return;

        activateCartStorePickup();
      })
      .catch(function () {
        if (!isHandoff) return;
        activateCartStorePickup();
      });
  }

  function isO2oCartHandoffUrl() {
    return /[?&]o2o=1(?:&|$)/.test(window.location.search);
  }

  function hideO2oCartBanner() {
    var el = document.querySelector('[data-o2o-cart-banner]');
    if (!el) return;
    el.hidden = true;
    el.innerHTML = '';
    el.setAttribute('data-o2o-cart-bound', 'true');
  }

  function initCartStoreBanner() {
    hideO2oCartBanner();
  }

  function autoSelectFirstOrderable(root) {
    if (!root._o2oLastRows) return;
    for (var i = 0; i < root._o2oLastRows.length; i++) {
      if (isOrderableStatus(root._o2oLastRows[i].status)) {
        setSelectedStoreRow(root, i);
        return;
      }
    }
    setSelectedStoreRow(root, null);
  }

  function filterStoreRows(root, query) {
    root._o2oSearchQuery = query || '';
    applyListFilters(root);
  }

  function getOrderQuantity() {
    var qtyInput = document.querySelector('#quantity');
    if (!qtyInput) return 1;
    var q = parseInt(qtyInput.value, 10);
    return q > 0 ? q : 1;
  }

  function fetchCartState() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function findCartLineForVariant(cart, variantId) {
    var vid = Number(variantId);
    if (!cart || !cart.items || !vid) return null;
    for (var i = 0; i < cart.items.length; i++) {
      if (Number(cart.items[i].variant_id) === vid) {
        return { line: i + 1, item: cart.items[i] };
      }
    }
    return null;
  }

  /** Properties update must use line or key — variant id alone fails on modern Shopify carts. */
  function changeCartLine(lineInfo, quantity, properties) {
    var body = {
      quantity: quantity,
      properties: properties,
    };
    if (lineInfo && lineInfo.line) {
      body.line = lineInfo.line;
    } else if (lineInfo && lineInfo.item && lineInfo.item.key) {
      body.id = lineInfo.item.key;
    } else {
      return Promise.reject(new Error('Grozā nav atrasta šī prece.'));
    }
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (data) {
        if (!r.ok) {
          throw new Error(
            translateCartApiError(data && (data.description || data.message || data.status))
          );
        }
        return data;
      });
    });
  }

  function translateCartApiError(message) {
    var m = String(message || '').trim();
    if (!m) return 'Neizdevās pievienot preci grozam.';
    if (/maximum quantity.*already in your cart/i.test(m)) {
      return 'Šī prece jau ir grozā. Turpinām noformēšanu grozā.';
    }
    if (/expected Integer/i.test(m) || m.indexOf('(?-mix') >= 0) {
      return 'Neizdevās atjaunināt grozu. Mēģiniet vēlreiz vai atveriet grozu.';
    }
    if (/is already sold out|sold out/i.test(m)) {
      return 'Šis variants ir izpārdots.';
    }
    if (/cannot find variant|variant not found/i.test(m)) {
      return 'Produkta variants nav atrasts.';
    }
    if (/^[a-z_]+ is not|^expected /i.test(m)) {
      return 'Neizdevās noformēt pasūtījumu. Pārbaudiet grozu un mēģiniet vēlreizi.';
    }
    return m;
  }

  function lineHasO2oProps(item) {
    if (!item || !item.properties) return false;
    for (var i = 0; i < O2O_LINE_PROP_KEYS.length; i++) {
      var k = O2O_LINE_PROP_KEYS[i];
      if (item.properties[k] != null && String(item.properties[k]).trim() !== '') {
        return true;
      }
    }
    return false;
  }

  function clearO2oPropsOnLine(lineInfo, item) {
    var cleared = {};
    Object.keys(item.properties || {}).forEach(function (k) {
      if (k.charAt(0) === '_') return;
      if (O2O_LINE_PROP_KEYS.indexOf(k) >= 0) cleared[k] = '';
    });
    if (!Object.keys(cleared).length) return Promise.resolve(false);
    return changeCartLine(lineInfo, item.quantity, cleared).then(function () {
      return true;
    });
  }

  function stripO2oLinePropertiesFromCart(cart) {
    var tasks = [];
    (cart.items || []).forEach(function (item, i) {
      if (!lineHasO2oProps(item)) return;
      tasks.push(clearO2oPropsOnLine({ line: i + 1, item: item }, item));
    });
    if (!tasks.length) return Promise.resolve(false);
    return Promise.all(tasks).then(function (results) {
      return results.some(Boolean);
    });
  }

  function goToO2oCart() {
    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    window.location.assign(cartUrl);
  }

  function submitQuickOrder(root) {
    var selection = root._o2oSelection || genericOrderContext(root);

    var variantId = getActiveVariantId(root);
    if (!variantId) {
      alert('Izvēlieties produkta variantu.');
      return;
    }

    setOrderButtonsLoading(root, true);

    fetchCartState()
      .then(function (cart) {
        var existing = findCartLineForVariant(cart, variantId);
        if (existing) {
          return changeCartLine(existing, existing.item.quantity, {});
        }

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: getOrderQuantity(),
              },
            ],
          }),
        }).then(function (r) {
          return r.json().then(function (data) {
            if (!r.ok) {
              var raw = (data && (data.description || data.message)) || '';
              if (/maximum quantity.*already in your cart/i.test(raw)) {
                return fetchCartState().then(function (cartAgain) {
                  var lineInfo = findCartLineForVariant(cartAgain, variantId);
                  if (!lineInfo) {
                    throw new Error(translateCartApiError(raw));
                  }
                  return changeCartLine(lineInfo, lineInfo.item.quantity, {});
                });
              }
              throw new Error(translateCartApiError(raw));
            }
            return data;
          });
        });
      })
      .then(function () {
        return new Promise(function (resolve) {
          setTimeout(resolve, 350);
        });
      })
      .then(function () {
        goToO2oCart();
      })
      .catch(function (err) {
        setOrderButtonsLoading(root, false);
        syncPrimaryCta(root);
        alert(
          translateCartApiError(err && err.message) ||
            'Neizdevās noformēt pasūtījumu. Pārbaudiet variantu un mēģiniet vēlreizi.'
        );
      });
  }

  function bindStorePicker(root) {
    if (root._o2oPickerBound) return;
    root._o2oPickerBound = true;

    root.addEventListener('click', function (e) {
      if (e.target.closest('[data-o2o-order]')) {
        e.preventDefault();
        submitQuickOrder(root);
        return;
      }
      if (e.target.closest('[data-o2o-availability-btn]')) {
        e.preventDefault();
        if (root._o2oOpenModal) root._o2oOpenModal();
        return;
      }
      if (
        e.target.closest('[data-o2o-primary]') ||
        e.target.closest('[data-o2o-sticky-action]')
      ) {
        e.preventDefault();
        handlePrimaryAction(root);
        return;
      }
      if (e.target.closest('[data-o2o-home-delivery]')) {
        e.preventDefault();
        var sid = root.getAttribute('data-section-id') || '';
        var atc = document.getElementById('AddToCart-' + sid);
        if (atc && !atc.disabled) atc.click();
        return;
      }
      if (e.target.closest('[data-o2o-saved-change]')) {
        e.preventDefault();
        if (root._o2oOpenModal) root._o2oOpenModal();
        return;
      }

    });

    var search = root.querySelector('[data-o2o-search]');
    if (!root._o2oSearchDelegated) {
      root._o2oSearchDelegated = true;
      root.addEventListener('input', function (e) {
        if (e.target && e.target.matches('[data-o2o-search]')) {
          filterStoreRows(root, e.target.value);
        }
      });
    }

  }

  function buildStoreRows(root, coordsMap, userCoords, options) {
    options = options || {};
    var payload = readShopifyPayload(root);
    var variantId =
      root.getAttribute('data-variant-id') || payload.variantId || '';
    var source = getVariantSource(payload, variantId);
    var onlineAvailable = root.getAttribute('data-online-available') === 'true';
    var now = getRigaNow();
    var listEl = root.querySelector('[data-o2o-store-list]');
    var storesBtn = root.querySelector('[data-o2o-stores-btn]');
    var preserveUi = !!options.preserveUiState;
    if (!root._o2oListFilter) root._o2oListFilter = 'all';
    if (!preserveUi) root._o2oSearchQuery = '';
    if (!listEl || !coordsMap.list) return { now: 0, transfer: 0, total: 0 };

    var rows = [];
    var nowCount = 0;
    var closedStockCount = 0;
    var transferCount = 0;

    coordsMap.list.forEach(function (store) {
      var resolved = resolveStatus(
        store,
        source.locations,
        source.inventory,
        onlineAvailable,
        now
      );
      if (resolved.status === 'now') nowCount++;
      if (resolved.status === 'closed_stock') closedStockCount++;
      if (resolved.status === 'transfer') transferCount++;

      var c = coordsForStore(store, coordsMap);
      var dist = null;
      if (c && userCoords && c.lat != null && c.lng != null) {
        dist = haversineKm(userCoords.lat, userCoords.lng, c.lat, c.lng);
      }

      var stockQty = stockQtyAtStore(store, source.inventory);
      if (resolved.matched && resolved.matched.available && stockQty === 0) {
        stockQty = 1;
      }

      rows.push({
        store: store,
        status: resolved.status,
        matched: resolved.matched,
        opensLabel: resolved.opensLabel,
        soonClose: resolved.soonClose,
        dist: dist,
        stockQty: stockQty,
        stockTier: computeStockTier(resolved.status, stockQty),
      });
    });

    rows.forEach(function (row) {
      row.city = cityForRow(row);
    });

    rows.sort(function (a, b) {
      var cityCmp = (a.city || '').localeCompare(b.city || '', 'lv');
      if (cityCmp !== 0) return cityCmp;
      var order = { now: 0, closed_stock: 1, transfer: 2, unavailable: 3 };
      if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
      if (a.dist != null && b.dist != null) return a.dist - b.dist;
      if (a.dist != null) return -1;
      if (b.dist != null) return 1;
      return (a.store.name || '').localeCompare(b.store.name || '', 'lv');
    });

    listEl.classList.remove('is-loading');
    listEl.innerHTML = '';
    root._o2oLastRows = rows;

    var searchInput = root.querySelector('[data-o2o-search]');
    if (searchInput) {
      searchInput.value = preserveUi || options.preserveSearch ? root._o2oSearchQuery || '' : '';
    }

    var lastCity = '';
    rows.forEach(function (row, idx) {
      var store = row.store;
      var orderable = isOrderableStatus(row.status);
      var city = row.city || cityForRow(row);

      if (city !== lastCity) {
        lastCity = city;
        var group = document.createElement('li');
        group.className = 'o2o-store-group';
        group.setAttribute('data-city-header', city);
        group.setAttribute('role', 'presentation');
        group.textContent = city;
        listEl.appendChild(group);
      }

      var li = document.createElement('li');
      li.className = 'o2o-store-row o2o-store-row--readonly';
      if (!orderable) li.classList.add('o2o-store-row--unavailable');
      li.setAttribute('data-store-row', '');
      li.setAttribute('data-row-index', String(idx));
      li.setAttribute('data-city', city);
      li.setAttribute('data-location-name', store.name || '');
      li.setAttribute('data-status', row.status);
      li.setAttribute('role', 'listitem');

      var venue = venueLineForStore(store);
      var addr = addressLineForRow(row);

      li.innerHTML =
        '<div class="o2o-store-row__place">' +
        '<p class="o2o-store-row__venue">' +
        escapeHtml(venue) +
        '</p>' +
        (addr
          ? '<p class="o2o-store-row__addr">' + escapeHtml(addr) + '</p>'
          : '') +
        '</div>' +
        '<div class="o2o-store-row__avail">' +
        availabilityPillHtml(row) +
        '</div>';

      listEl.appendChild(li);
    });

    var total = rows.length;

    bindStorePicker(root);
    bindModalFilters(root);
    renderModalFilters(root, {
      now: nowCount,
      closedStock: closedStockCount,
      transfer: transferCount,
      total: total,
    });
    applyListFilters(root);
    if (!options.preserveSelection) {
      root._o2oSelection = null;
    }
    syncModalFooter(root);

    var pickupStores = nowCount + closedStockCount;

    if (storesBtn) {
      storesBtn.hidden = true;
    }

    syncHero(root, nowCount, closedStockCount, transferCount, total, onlineAvailable);
    root.setAttribute('data-pickup-now', String(nowCount));
    updateSavedStoreChip(root);
    syncPrimaryCta(root);

    return { now: nowCount, transfer: transferCount, total: total };
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function syncHero(root, nowCount, closedStockCount, transferCount, total, onlineAvailable) {
    root._o2oHeroCounts = {
      now: nowCount,
      closedStock: closedStockCount,
      transfer: transferCount,
      total: total,
      online: onlineAvailable,
    };
    syncHeroFromSelection(root);
    syncModalTitle(root);
    syncPrimaryCta(root);
  }

  function bindModal(root) {
    if (!root.querySelector('[data-o2o-modal]')) return;

    var storesUrl = root.getAttribute('data-stores-json') || '';
    var sectionId = root.getAttribute('data-section-id') || '';

    function getModal() {
      return root.querySelector('[data-o2o-modal]');
    }

    function setAvailabilityExpanded(open) {
      var btn = root.querySelector('[data-o2o-availability-btn]');
      if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    function closeModal() {
      var modal = getModal();
      if (!modal) return;
      modal.hidden = true;
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('o2o-modal-open');
      setAvailabilityExpanded(false);
      root._o2oSearchQuery = '';
      var search = root.querySelector('[data-o2o-search]');
      if (search) search.value = '';
      if (root._o2oRefreshSticky) root._o2oRefreshSticky();
    }

    root._o2oCloseModal = closeModal;

    if (!root._o2oModalBound) {
      root._o2oModalBound = true;

      root.addEventListener('click', function (e) {
        if (e.target.closest('[data-o2o-close]') || e.target.closest('[data-o2o-backdrop]')) {
          e.preventDefault();
          closeModal();
        }
      });

      if (!root._o2oEscBound) {
        root._o2oEscBound = true;
        document.addEventListener('keydown', function (e) {
          if (e.key !== 'Escape') return;
          document.querySelectorAll('[data-o2o-root]').forEach(function (r) {
            var m = r.querySelector('[data-o2o-modal]');
            if (m && !m.hidden && r._o2oCloseModal) r._o2oCloseModal();
          });
        });
      }
    }

    root._o2oOpenModal = function openModal() {
      var modal = getModal();
      if (!modal) return;
      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      setAvailabilityExpanded(true);
      document.documentElement.classList.add('o2o-modal-open');
      if (root._o2oHideSticky) root._o2oHideSticky();
      var first = modal.querySelector('[data-o2o-close], [data-o2o-backdrop]');
      if (first) first.focus();

      Promise.all([loadStoreCoords(storesUrl), fetchApproxCoordsByIp()]).then(function (res) {
        buildStoreRows(root, res[0], coordsForSorting());
      });
    };

    bindModalFilters(root);

    root._o2oRefresh = function (variantId) {
      if (!variantId || !sectionId) return;
      var modal = getModal();
      var modalWasOpen = modal && !modal.hidden;
      var prevFilter = root._o2oListFilter || 'all';
      var prevSearch = root._o2oSearchQuery || '';
      var prevSelection = root._o2oSelection;
      var url =
        window.location.pathname +
        '?variant=' +
        encodeURIComponent(variantId) +
        '&section_id=' +
        encodeURIComponent(sectionId);
      fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) {
          return r.text();
        })
        .then(function (html) {
          var doc = document.createElement('div');
          doc.innerHTML = html;
          var fresh = doc.querySelector('[data-o2o-root]');
          if (!fresh || !root) return;

          var freshJson = fresh.querySelector('[data-o2o-shopify-json]');
          var curJson = root.querySelector('[data-o2o-shopify-json]');
          if (freshJson && curJson) {
            curJson.textContent = freshJson.textContent;
          }

          root.setAttribute('data-variant-id', String(variantId));
          root.setAttribute(
            'data-online-available',
            fresh.getAttribute('data-online-available') || 'false'
          );
          root.setAttribute('data-pickup-now', fresh.getAttribute('data-pickup-now') || '0');

          root._o2oListFilter = prevFilter;
          root._o2oSearchQuery = prevSearch;

          return Promise.all([loadStoreCoords(storesUrl), fetchApproxCoordsByIp()]).then(function (res) {
            buildStoreRows(root, res[0], coordsForSorting(), {
              preserveUiState: true,
              preserveSearch: true,
              preserveSelection: true,
            });
            if (prevSelection && prevSelection.normName && root._o2oLastRows) {
              var idx = findRowIndexByNormName(root._o2oLastRows, prevSelection.normName);
              if (idx >= 0 && isOrderableStatus(root._o2oLastRows[idx].status)) {
                setSelectedStoreRow(root, idx);
              }
            }
            if (modalWasOpen && modal) {
              modal.hidden = false;
              modal.setAttribute('aria-hidden', 'false');
              document.documentElement.classList.add('o2o-modal-open');
              setAvailabilityExpanded(true);
            }
            var searchInput = root.querySelector('[data-o2o-search]');
            if (searchInput) searchInput.value = prevSearch;
            applyListFilters(root);
          });
        })
        .catch(function () {});
    };
  }

  function initAll() {
    document.querySelectorAll('[data-o2o-root]').forEach(function (root) {
      if (root.getAttribute('data-o2o-bound') === 'true') return;
      root.setAttribute('data-o2o-bound', 'true');
      bindStorePicker(root);
      bindModal(root);
      initStickyBar(root);
      var storesUrl = root.getAttribute('data-stores-json') || '';
      Promise.all([loadStoreCoords(storesUrl), fetchApproxCoordsByIp()]).then(function (res) {
        buildStoreRows(root, res[0], coordsForSorting());
      });
    });
    initCartStoreBanner();
    initO2oCartHandoff();
  }

  function onVariantChange(evt) {
    var variant = evt && evt.variant;
    var vid = variant && variant.id;
    if (!vid) return;
    if (onVariantChange._timer) window.clearTimeout(onVariantChange._timer);
    onVariantChange._timer = window.setTimeout(function () {
      document.querySelectorAll('[data-o2o-root]').forEach(function (root) {
        if (root._o2oRefresh) root._o2oRefresh(vid);
      });
    }, 80);
  }

  window.AIOO_O2O = {
    init: initAll,
    onVariantChange: onVariantChange,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (window.jQuery) {
    window.jQuery(document).on('variantChange', function (e) {
      var v = e && e.variant;
      if (v) onVariantChange({ variant: v });
    });
    window.jQuery('product-form, .product-single').on('variantChange', function (e) {
      var v = e && e.variant;
      if (v) onVariantChange({ variant: v });
    });
  }
})(window);
